import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { PieChart } from "@mui/x-charts/PieChart";
import stilePulsanti from "../interfaccia/stilePulsanti";
import { BlockSkeleton, TextSkeleton } from "../interfaccia/loading";
import { PUBLISH_STATUS_COLORS } from "./publicationStatus";


const FACET_COLOR_PALETTE = [
  "#1976d2",
  "#43a047",
  "#fb8c00",
  "#8e24aa",
  "#ef5350",
  "#00acc1",
  "#7cb342",
  "#6d4c41",
  "#c2185b",
  "#5c6bc0",
  "#f9a825",
  "#00897b",
];

const IMAGE_PIE_COLORS = ["#1976d2", "#90caf9"];

function formatNumber(value) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "0";
  }
  return value.toLocaleString("it-IT");
}

function normalizeLabel(value, fallback = "Senza valore") {
  if (value === null || typeof value === "undefined") {
    return fallback;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : fallback;
  }
  return String(value);
}

function buildPieDataset(items = [], palette = FACET_COLOR_PALETTE) {
  if (!Array.isArray(items)) {
    return [];
  }
  let colorIndex = 0;
  return items
    .filter((item) => Number(item?.count ?? 0) > 0)
    .map((item, idx) => {
      const color = palette[colorIndex % palette.length];
      colorIndex += 1;
      return {
        id: item.label ?? `slice-${idx}`,
        label: normalizeLabel(item.label),
        value: Number(item.count ?? 0),
        color,
      };
    });
}

function ModaArticoliStatsSkeleton() {
  return (
    <div className="mt3">
      <div className="flex flex-wrap">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={`pie-card-${index}`}
            className="pa3 ma2 br3 ba b--black-10 bg-white flex-auto"
            style={{ minWidth: "280px" }}
          >
            <BlockSkeleton width={180} height={22} className="mb3" />
            <BlockSkeleton
              width={220}
              height={220}
              borderRadius="50%"
              className="center"
            />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={`info-card-${index}`}
            className="pa3 ma2 br3 ba b--black-10 bg-white flex-auto"
            style={{ minWidth: "220px" }}
          >
            <BlockSkeleton width={140} height={20} className="mb3" />
            <TextSkeleton lines={4} lineHeight={14} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ModaArticoliStatsContent({
  stats,
  loading,
  error,
  onRefresh,
}) {
  const { buttonClassesWhite } = stilePulsanti;

  const publishStatusData = useMemo(() => {
    if (!stats?.publishStatus) {
      return [];
    }
    return stats.publishStatus
      .filter((item) => Number(item?.count ?? 0) > 0)
      .map((item, index) => {
        const key = item?.label ? item.label.toUpperCase() : "ALTRO";
        const color =
          PUBLISH_STATUS_COLORS[key] ??
          PUBLISH_STATUS_COLORS.ALTRO ??
          FACET_COLOR_PALETTE[index % FACET_COLOR_PALETTE.length];
        return {
          id: key || `status-${index}`,
          label: normalizeLabel(item.label, "Altro"),
          value: Number(item.count ?? 0),
          color,
        };
      });
  }, [stats]);

  const imagesData = useMemo(() => {
    if (!stats?.images) {
      return [];
    }
    const dataset = [];
    if (stats.images.with > 0) {
      dataset.push({
        id: "withImages",
        label: "Con immagini",
        value: Number(stats.images.with),
        color: IMAGE_PIE_COLORS[0],
      });
    }
    if (stats.images.without > 0) {
      dataset.push({
        id: "withoutImages",
        label: "Senza immagini",
        value: Number(stats.images.without),
        color: IMAGE_PIE_COLORS[1],
      });
    }
    return dataset;
  }, [stats]);

  const reasonData = useMemo(
    () => buildPieDataset(stats?.deactivationReasons),
    [stats]
  );

  const facetConfigs = useMemo(
    () => [
      { title: "Reparti", data: stats?.reparti },
      { title: "Categorie", data: stats?.categorie },
      { title: "Tipi", data: stats?.tipi },
      { title: "Persone", data: stats?.persone },
      { title: "Stagioni", data: stats?.stagioni },
    ],
    [stats]
  );

  const infoCards = [
    {
      title: "Visibilità",
      rows: [
        { label: "Visibili", value: stats?.visibility?.visible ?? 0 },
        { label: "Non visibili", value: stats?.visibility?.hidden ?? 0 },
      ],
    },
    {
      title: "Attivazione",
      rows: [
        { label: "Attivi", value: stats?.active?.active ?? 0 },
        { label: "Non attivi", value: stats?.active?.inactive ?? 0 },
      ],
    },
    {
      title: "Stock",
      rows: [
        { label: "Stock > 0", value: stats?.stock?.positive ?? 0 },
        {
          label: "Stock < 1",
          value: stats?.stock?.zeroOrNegative ?? 0,
        },
      ],
    },
    {
      title: "Immagine non trovata",
      rows: [{ label: "Totale", value: stats?.missingImageFlag ?? 0 }],
    },
    {
      title: "Senza ID PS",
      rows: [{ label: "Totale", value: stats?.missingIdPs ?? 0 }],
    },
  ];

  const renderPieCard = (title, data, emptyLabel = "Nessun dato") => (
    <div className="w-100 w-50-ns w-third-l pa2" key={title}>
      <div className="pa3 br3 ba b--black-10 bg-white h-100 flex flex-column">
        <h4 className="mt0 mb3">{title}</h4>
        {data.length > 0 ? (
          <>
            <PieChart
              height={220}
              series={[
                {
                  data,
                  innerRadius: 40,
                  outerRadius: 90,
                  paddingAngle: 2,
                  cornerRadius: 2,
                },
              ]}
              slotProps={{ legend: { hidden: true } }}
            />
            <div className="mt2">
              {data.map((item) => (
                <div
                  key={item.id ?? item.label}
                  className="flex items-center f7 dark-gray mb1"
                >
                  <span
                    className="dib br-100 mr2"
                    style={{
                      width: "10px",
                      height: "10px",
                      backgroundColor: item.color,
                    }}
                  />
                  <span>{item.label}</span>
                  <span className="ml1 gray">
                    ({formatNumber(item.value)})
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="gray f6">{emptyLabel}</div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between">
        <div className="mr3">
          <h3 className="mt0 mb1">Statistiche selezione</h3>
          <div className="gray f6">
            Totale articoli: <strong>{formatNumber(stats?.total ?? 0)}</strong>
          </div>
          <div className="gray f7">
            Basate sui filtri attualmente applicati.
          </div>
        </div>
        <div className="flex items-center">
          <button
            type="button"
            className={buttonClassesWhite}
            onClick={onRefresh}
            disabled={loading}
          >
            Aggiorna
          </button>
        </div>
      </div>
      {error && (
        <div className="mv3 pa3 br2 bg-washed-red dark-red">{error}</div>
      )}
      {loading ? (
        <div className="mv4">
          <ModaArticoliStatsSkeleton />
        </div>
      ) : (
        <div className="mt3">
          <div className="flex flex-wrap">
            {renderPieCard("Stato pubblicazione", publishStatusData)}
            {renderPieCard("Disponibilità immagini", imagesData)}
            {renderPieCard("Motivo disattivazione", reasonData)}
          </div>
          <div className="flex flex-wrap">
            {infoCards.map((card) => (
              <div
                key={card.title}
                className="pa3 ma2 br3 ba b--black-10 bg-white flex-auto"
                style={{ minWidth: "220px" }}
              >
                <h4 className="mt0 mb2">{card.title}</h4>
                {card.rows.map((row) => (
                  <div
                    className="flex justify-between f6"
                    key={`${card.title}-${row.label}`}
                  >
                    <span>{row.label}</span>
                    <span className="b">{formatNumber(row.value)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap">
            {facetConfigs.map((facet) =>
              renderPieCard(
                facet.title,
                buildPieDataset(facet.data),
                "Nessun valore disponibile"
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ModaArticoliStatsModal({
  open,
  onClose,
  stats,
  loading,
  error,
  onRefresh,
}) {
  const hasDocument = typeof document !== "undefined";

  if (!open || !hasDocument) {
    return null;
  }

  return createPortal(
    <div className="modal" onClick={onClose}>
      <div
        className="w-100 w-90-ns w-80-l center pa3 pa4-ns br3 shadow-4 sfondoBiancoTema relative"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="bn bg-transparent absolute top-1 right-1 pointer f3"
          onClick={onClose}
          aria-label="Chiudi"
        >
          ×
        </button>
        <ModaArticoliStatsContent
          stats={stats}
          loading={loading}
          error={error}
          onRefresh={onRefresh}
        />
      </div>
    </div>,
    document.body
  );
}

export default ModaArticoliStatsModal;
